import type {
  GridAggregationFunction,
  GridSortDirection,
} from '@mui/x-data-grid-premium';

interface CostBasisFields {
  Balance?: number | null;
  CostBasis?: number | null;
}

type OptionalNumber = number | null | undefined;

export const hasUsableCostBasis = <T extends CostBasisFields>(
  holding: T,
): holding is T & { Balance: number; CostBasis: number } =>
  holding.CostBasis != null &&
  holding.CostBasis !== 0 &&
  holding.Balance != null;

// The API computes Balance - CostBasis for any cost basis, including $0, so a
// holding without a usable basis would otherwise show its whole balance.
export const getUnrealizedGains = (
  holding: CostBasisFields & { UnrealizedGains?: number },
) => (hasUsableCostBasis(holding) ? holding.UnrealizedGains : undefined);

export const unrealizedGainsAggregation: GridAggregationFunction<
  OptionalNumber,
  number | null
> = {
  apply: ({ values }) => {
    const gains = values.filter(
      (value): value is number =>
        typeof value === 'number' && !Number.isNaN(value),
    );
    if (gains.length === 0) return null;
    return gains.reduce((sum, gain) => sum + gain, 0);
  },
  columnTypes: ['number'],
};

export const getNullsLastSortComparator =
  (direction: GridSortDirection) => (a: OptionalNumber, b: OptionalNumber) => {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    return direction === 'desc' ? b - a : a - b;
  };

export const formatUnrealizedGainsForExport = (value: OptionalNumber) =>
  value == null ? 'N/A' : value.toLocaleString();
